import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { ScanBatchProgress, ScanBatchStatus } from '../types/scan';

const ACTIVE_BATCH_STATUSES: ScanBatchStatus[] = ['queued', 'creating_scans', 'scanning', 'assessing'];
const POLL_INTERVAL_MS = 3000;

export interface ScanBatchProgressState {
  percentComplete: number;
  findingsCount: number;
  currentOperation: string;
  status: ScanBatchStatus | null;
  loading: boolean;
}

/**
 * Poll a single scan batch's live progress while it is active.
 *
 * Used to show inline progress on the scans list (and feeds the repository-wide
 * progress banner). Polling stops automatically once the batch reaches a terminal
 * status or when `batchId` is cleared.
 */
export const useScanBatchProgress = (batchId: string | null | undefined): ScanBatchProgressState => {
  const api = useAuthenticatedApi();
  const [progress, setProgress] = useState<ScanBatchProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchProgress = useCallback(async () => {
    if (!batchId) return;
    try {
      const response = await api.get<ScanBatchProgress>(apiEndpoints.scans.getBatchProgress(batchId));
      if (isMountedRef.current) setProgress(response);
    } catch (err) {
      console.error('Error fetching scan batch progress:', err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [api, batchId]);

  // Reset + initial fetch whenever the batch changes.
  useEffect(() => {
    if (!batchId) {
      setProgress(null);
      return;
    }
    setLoading(true);
    fetchProgress();
  }, [batchId, fetchProgress]);

  const status = progress?.status ?? null;
  // Poll until we know the batch is terminal (null status = not loaded yet, keep polling).
  const isActive = status === null || ACTIVE_BATCH_STATUSES.includes(status);

  useEffect(() => {
    if (!batchId || !isActive) return;
    const interval = window.setInterval(fetchProgress, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [batchId, isActive, fetchProgress]);

  return {
    percentComplete: progress?.percent_complete ?? 0,
    findingsCount: progress?.finding_counts?.actionable ?? 0,
    currentOperation: progress?.current_operation ?? '',
    status,
    loading,
  };
};

export default useScanBatchProgress;
